import { useState } from "react";
import { AnaliseSCR, IConsultaSCR, OperacaoSCR } from "../../../../interface/ConsultaSCR";
import { formatarParaMoedaBRL } from "../../../../utils";
import { maskCpfCnpj } from "../../../../utils";
import { percentFormatter } from "../../../../utils/formatters";
import "./ConsultaSCR.css";

const DOMINIO_VENCIMENTOS: Record<string, string> = {
  "20": "Limite de crédito com vencimento até 360 dias",
  "40": "Limite de crédito com vencimento acima de 360 dias",
  "60": "Créditos a liberar até 360 dias",
  "80": "Créditos a liberar acima de 360 dias",
  "110": "Créditos a vencer até 30 dias",
  "120": "Créditos a vencer de 31 a 60 dias",
  "130": "Créditos a vencer de 61 a 90 dias",
  "140": "Créditos a vencer de 91 a 180 dias",
  "150": "Créditos a vencer de 181 a 360 dias",
  "160": "Créditos a vencer de 361 a 720 dias",
  "165": "Créditos a vencer de 721 a 1080 dias",
  "170": "Créditos a vencer de 1081 a 1440 dias",
  "175": "Créditos a vencer de 1441 a 1800 dias",
  "180": "Créditos a vencer de 1801 a 5400 dias",
  "190": "Créditos a vencer acima de 5400 dias",
  "199": "Créditos a vencer com prazo indeterminado",
  "205": "Créditos vencidos de 1 a 14 dias",
  "210": "Créditos vencidos de 15 a 30 dias",
  "220": "Créditos vencidos de 31 a 60 dias",
  "230": "Créditos vencidos de 61 a 90 dias",
  "240": "Créditos vencidos de 91 a 120 dias",
  "245": "Créditos vencidos de 121 a 150 dias",
  "250": "Créditos vencidos de 151 a 180 dias",
  "255": "Créditos vencidos de 181 a 240 dias",
  "260": "Créditos vencidos de 241 a 300 dias",
  "270": "Créditos vencidos de 301 a 360 dias",
  "280": "Créditos vencidos de 361 a 540 dias",
  "290": "Créditos vencidos acima de 540 dias",
  "310": "Créditos baixados como prejuízo até 12 meses",
  "320": "Créditos baixados como prejuízo há mais de 12m e até 48 meses",
  "330": "Créditos baixados como prejuízo há mais de 48 meses",
};

const DOMINIO_MODALIDADES: Record<string, string> = {
  "01": "Adiantamentos a depositantes",
  "02": "Empréstimos",
  "03": "Direitos creditórios descontados",
  "04": "Financiamentos",
  "05": "Financiamentos à exportação",
  "06": "Financiamentos à importação",
  "07": "Financiamentos com interveniência",
  "08": "Financiamentos rurais",
  "09": "Financiamentos imobiliários",
  "10": "Financiamentos de títulos e valores mobiliários",
  "11": "Financiamentos de infraestrutura e desenvolvimento",
  "12": "Operações de arrendamento",
  "13": "Outros créditos",
  "14": "Relações interfinanceiras",
  "15": "Fiança bancária e garantias prestadas",
  "18": "Títulos com risco de crédito",
  "19": "Limite (contratado e não utilizado)",
  "20": "Retenção de risco",
};

const DOMINIO_SUBMODALIDADES: Record<string, string> = {
  "0101": "Adiantamentos a depositantes",
  "0202": "Crédito pessoal – com consignação em folha de pagamento",
  "0203": "Crédito pessoal – sem consignação em folha de pagamento",
  "0204": "Crédito rotativo vinculado a cartão de crédito",
  "0209": "ARO – adiantamento de receitas orçamentárias",
  "0210": "Cartão de crédito – compra, fatura parcelada ou saque financiados pela instituição emitente",
  "0211": "Home equity",
  "0212": "Microcrédito",
  "0213": "Cheque especial",
  "0214": "Conta garantida",
  "0215": "Capital de giro com prazo de vencimento até 365 dias",
  "0216": "Capital de giro com prazo de vencimento superior a 365 dias",
  "0217": "Capital de giro rotativo",
  "0218": "Cartão de crédito – não migrado",
  "0250": "Recebíveis adquiridos",
  "0299": "Outros empréstimos",
  "0301": "Desconto de duplicatas",
  "0302": "Desconto de cheques",
  "0303": "Antecipação de fatura de cartão de crédito",
  "0398": "Outros direitos creditórios descontados",
  "0399": "Outros títulos descontados",
  "0401": "Aquisição de bens – veículos automotores",
  "0402": "Aquisição de bens – outros bens",
  "0403": "Microcrédito",
  "0404": "Vendor",
  "0405": "Compror",
  "0406": "Cartão de crédito – compra ou fatura parcelada pela instituição emitente",
  "0440": "Financiamentos agroindustriais",
  "0450": "Recebíveis adquiridos",
  "0490": "Financiamento de projeto",
  "0499": "Outros financiamentos",
  "0501": "Financiamento à exportação",
  "0502": "Adiantamento sobre contratos de câmbio (ACC)",
  "0503": "Adiantamento sobre cambiais entregues (ACE)",
  "0504": "Créditos decorrentes de contratos de exportação (export note)",
  "0590": "Financiamento de projeto",
  "0599": "Outros financiamentos à exportação",
  "0601": "Financiamento à importação",
  "0701": "Aquisição de bens com interveniência – veículos automotores",
  "0702": "Aquisição de bens com interveniência – outros bens",
  "0799": "Outros financiamentos com interveniência",
  "0801": "Custeio",
  "0802": "Investimento",
  "0803": "Comercialização",
  "0804": "Industrialização",
  "0901": "Financiamento habitacional – SFH",
  "0902": "Financiamento habitacional – exceto SFH",
  "0903": "Financiamento imobiliário – empreendimentos, exceto habitacional",
  "0990": "Financiamento de projeto",
  "1001": "Financiamento de TVM",
  "1101": "Financiamento de infraestrutura e desenvolvimento",
  "1190": "Financiamento de projeto",
  "1201": "Arrendamento financeiro, exceto veículos automotores e imóveis",
  "1202": "Arrendamento financeiro imobiliário",
  "1205": "Arrendamento operacional",
  "1206": "Arrendamento financeiro de veículos automotores",
  "1301": "Avais e fianças honrados",
  "1302": "Devedores por compra de valores e bens",
  "1303": "Títulos e créditos a receber",
  "1304": "Cartão de crédito – compra à vista e parcelado lojista",
  "1350": "Recebíveis adquiridos",
  "1399": "Outros com característica de crédito",
  "1401": "Repasses interfinanceiros",
  "1402": "Recebíveis de arranjo de pagamento",
  "1403": "Outros valores a receber relativos a transações de pagamento",
  "1501": "Beneficiários de garantias prestadas para operações com PJ financeira",
  "1502": "Beneficiários de garantias prestadas para operações com outras pessoas",
  "1503": "Beneficiários de garantias prestadas para fundos constitucionais",
  "1504": "Beneficiários de garantias prestadas para participação em processo licitatório",
  "1505": "Carta de crédito de importação",
  "1511": "Coobrigação assumida em cessão com coobrigação (pessoa SFN)",
  "1512": "Coobrigação assumida em cessão com coobrigação (pessoa não SFN, incl. securitizadoras/fundos)",
  "1513": "Beneficiários de outras coobrigações",
  "1514": "Garantia financeira vinculada a operações com IF no exterior",
  "1515": "Garantia financeira vinculada a operações com não financeiras",
  "1516": "Garantia vinculada a prestação de serviços/execução de obras (performance bonds)",
  "1517": "Garantia vinculada ao fornecimento de mercadorias",
  "1518": "Garantia vinculada a distribuição de TVM",
  "1519": "Aval/fiança em processos judiciais/administrativos fiscais",
  "1520": "Garantia vinculada ao comércio internacional de mercadorias",
  "1521": "Apoio em processos de securitização",
  "1590": "Financiamento de projeto",
  "1599": "Beneficiários de outras garantias prestadas",
  "1801": "CPR – Cédula de Produto Rural",
  "1802": "EN – Nota de Exportação",
  "1803": "Debêntures",
  "1804": "Notas Comerciais",
  "1899": "Outros",
  "1901": "Limite global",
  "1902": "Cheque especial",
  "1903": "Conta garantida",
  "1904": "Cartão de crédito",
  "1905": "Capital de giro",
  "1906": "Crédito pessoal",
  "1907": "Vendor",
  "1908": "Compror",
  "1909": "Descontos",
  "1910": "Aquisição de bens",
  "1999": "Outros limites",
  "2001": "Retenção de risco assumida por aquisição de cotas de fundos",
  "2002": "Retenção de risco assumida por aquisição de instrumentos com lastro em operações de crédito",
};

// Faixas do detalhamento "Carteira a Vencer" (padrão SCR: vencidos até 14 dias
// compõem a carteira a vencer junto com a faixa de até 30 dias)
const FAIXAS_CARTEIRA_VENCER = [
  { label: "Até 30 dias / Vencidos até 14 dias", codigos: [110, 205], cor: "#10b981" },
  { label: "31 a 60 dias", codigos: [120], cor: "#0d9488" },
  { label: "61 a 90 dias", codigos: [130], cor: "#2563eb" },
  { label: "91 a 180 dias", codigos: [140], cor: "#9333ea" },
  { label: "181 a 360 dias", codigos: [150], cor: "#6366f1" },
  { label: "Acima de 360 dias", codigos: [160, 165, 170, 175, 180, 190, 199], cor: "#e11d48" },
];

const TIPO_GARANTIA: Record<string, string> = {
  // Garantias Pessoais / Fidejussórias
  "1003": "Aval ou Fiança prestada por Pessoa Física",
  "1004": "Aval ou Fiança prestada por Pessoa Jurídica",
  "1005": "Aval ou Fiança prestada por Instituição Financeira",
  // Alienação Fiduciária
  "0901": "Alienação Fiduciária de Veículos",
  "0902": "Alienação Fiduciária de Bens Imóveis",
  "0903": "Alienação Fiduciária de outros bens móveis",
  // Direitos Creditórios e Aplicações
  "0423": "Direitos sobre contratos de cessão de bens/serviços",
  "0425": "Cessão fiduciária de recebíveis de arranjos de pagamento",
  "0430": "Cessão Fiduciária de Títulos de Crédito ou Direitos sobre Aplicações Financeiras",
  // Hipotecas
  "0671": "Hipoteca de Primeiro Grau de Imóvel Urbano",
  "0672": "Hipoteca de Primeiro Grau de Imóvel Rural",
  "0673": "Hipoteca de Segundo Grau (ou graus subsequentes) de Bens Imóveis",
  // Penhores
  "0199": "Outras garantias reais de Bens Móveis",
  "0350": "Penhor de Direitos e Títulos de Crédito",
  "0701": "Penhor Rural",
  "0702": "Penhor Industrial ou Mercantil",
  "0799": "Outros tipos de Penhor Corpóreo",
  // Seguros e Fundos Garantidores
  "0886": "Fundo Garantidor de Operações (FGO)",
  "0890": "Fundo Garantidor para Investimentos (FGI)",
  "0899": "Outros seguros de crédito ou assemelhados",
};

const descricaoModalidade = (mod: string): string => {
  const codigo = String(mod ?? "");
  return (
    DOMINIO_SUBMODALIDADES[codigo] ??
    DOMINIO_MODALIDADES[codigo.substring(0, 2)] ??
    `Modalidade ${codigo}`
  );
};

const ModalidadeCard = ({ mod, vencimentos }: { mod: string; vencimentos: Record<number, number> }) => {
  const [expandido, setExpandido] = useState(false);

  const linhas = Object.entries(vencimentos)
    .map(([codigo, valor]) => ({ codigo: Number(codigo), valor }))
    .sort((a, b) => a.codigo - b.codigo);
  const totalModalidade = linhas.reduce((soma, linha) => soma + linha.valor, 0);

  return (
    <div className="scr-modalidade-card">
      <div
        className="scr-modalidade-header scr-modalidade-header-toggle"
        onClick={() => setExpandido((atual) => !atual)}
      >
        <div className="scr-modalidade-title">
          <i className="pi pi-tag"></i> {descricaoModalidade(mod)} (cód. {mod})
        </div>
        <div className="scr-modalidade-total">
          {formatarParaMoedaBRL(totalModalidade)}
          <span className="scr-modalidade-toggle-icon">
            <i className={`pi ${expandido ? "pi-minus" : "pi-plus"}`}></i>
          </span>
        </div>
      </div>

      {expandido && (
        <div className="scr-info-list" style={{ gap: "8px" }}>
          {linhas.map((linha) => (
            <div key={linha.codigo} className="scr-modalidade-item">
              <div className="scr-modalidade-item-left">
                <span className="scr-modalidade-item-desc">
                  {DOMINIO_VENCIMENTOS[String(linha.codigo)] ?? `Código ${linha.codigo}`}
                </span>
              </div>
              <div className={linha.codigo >= 205 ? "scr-modalidade-item-value-vencido" : "scr-modalidade-item-value-avencer"}>
                {formatarParaMoedaBRL(linha.valor)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const formatarDataBase = (dtb?: number): string => {
  const texto = String(dtb ?? "");
  return texto.length === 6 ? `${texto.substring(4, 6)}/${texto.substring(0, 4)}` : texto || "Não informado";
};

const formatarDataISO = (data?: string): string => {
  if (!data) return "Não informado";
  const partes = data.split("-");
  return partes.length === 3 ? `${partes[2]}/${partes[1]}/${partes[0]}` : data;
};

const listarDataBase = (data: IConsultaSCR): AnaliseSCR[] => {
  const payload = data?.scrSeletor?.scrBacen?.payload;
  return payload?.lsDtb ?? (payload as any)?.analise ?? [];
};

export const temDadosSCR = (data: IConsultaSCR) => listarDataBase(data).length > 0;

export const ConsultaSCRPage = (data: IConsultaSCR) => {
  const payload = data?.scrSeletor?.scrBacen?.payload;
  const listaDataBase = listarDataBase(data);
  // const score = payload?.score;
  // const renda = payload?.renda;
  const documento = data?.informativo?.documento || payload?.cdCli;

  return (
    <>
      {/* {(score !== undefined && score !== null) || (renda !== undefined && renda !== null) ? (
        <div className="scr-cards-grid scr-section-mb">
          {score !== undefined && score !== null && (
            <div className="scr-card scr-card-blue">
              <div className="scr-card-header">
                <i className="pi pi-chart-line scr-card-icon"></i> SCORE
              </div>
              <div className="scr-card-value">{String(score)}</div>
              <div className="scr-card-label">score de comportamento</div>
            </div>
          )}
          {renda !== undefined && renda !== null && (
            <div className="scr-card scr-card-green">
              <div className="scr-card-header">
                <i className="pi pi-wallet scr-card-icon"></i> RENDA ESTIMADA
              </div>
              <div className="scr-card-value">{formatarParaMoedaBRL(Number(renda) || 0)}</div>
              <div className="scr-card-label">renda presumida</div>
            </div>
          )}
        </div>
      ) : null} */}

      {listaDataBase.map((dataBase, indice) => {
        const operacoes: OperacaoSCR[] = dataBase?.lsOp ?? [];

        const porCodigo: Record<number, number> = {};
        const modalidades: Record<string, Record<number, number>> = {};
        operacoes.forEach((operacao) => {
          const mod = String(operacao?.mod ?? "");
          Object.entries(operacao?.resVenc || {}).forEach(([chave, valor]) => {
            const codigo = Number(chave.replace(/^v/i, ""));
            const valorNumerico = Number(valor) || 0;
            porCodigo[codigo] = (porCodigo[codigo] || 0) + valorNumerico;
            if (!modalidades[mod]) {
              modalidades[mod] = {};
            }
            modalidades[mod][codigo] = (modalidades[mod][codigo] || 0) + valorNumerico;
          });
        });

        const garantias: Record<string, number> = {};
        operacoes.forEach((operacao) => {
          (operacao?.lsGar || []).forEach((g) => {
            if (g?.tp) {
              garantias[g.tp] = (garantias[g.tp] || 0) + (Number(g.qtd) || 0);
            }
          });
        });

        const somaCodigos = (filtro: (codigo: number) => boolean) =>
          Object.entries(porCodigo).reduce(
            (soma, [codigo, valor]) => (filtro(Number(codigo)) ? soma + valor : soma),
            0
          );

        const totalLimites = somaCodigos((c) => c <= 80);
        // Padrão SCR: vencidos até 14 dias (v205) compõem a carteira a vencer
        const totalAVencer = somaCodigos((c) => (c >= 110 && c <= 199) || c === 205);
        const totalVencido = somaCodigos((c) => c >= 210 && c <= 290);
        const totalPrejuizo = somaCodigos((c) => c >= 310);
        const riscoTotal = totalLimites + totalAVencer + totalVencido + totalPrejuizo;
        const carteiraCredito = totalAVencer + totalVencido + totalPrejuizo;
        const coobrigacaoAssumida = Number(dataBase?.coobAss) || 0;
        const coobrigacaoRecebida = Number(dataBase?.coobRec) || 0;
        const responsabilidadeTotal = carteiraCredito + coobrigacaoAssumida;
        const limitesAte360 = somaCodigos((c) => c === 20 || c === 60);
        const limitesAcima360 = somaCodigos((c) => c === 40 || c === 80);

        const modalidadesOrdenadas = Object.entries(modalidades).sort(([modA], [modB]) =>
          modA.localeCompare(modB)
        );
        const modalidadesLimite = modalidadesOrdenadas.filter(([mod]) => mod.startsWith("19"));
        const modalidadesOutras = modalidadesOrdenadas.filter(([mod]) => !mod.startsWith("19"));
        const totalLimiteModalidades = modalidadesLimite.reduce(
          (soma, [, vencimentos]) => soma + Object.values(vencimentos).reduce((s, v) => s + v, 0),
          0
        );

        const larguraBarra = (valor: number) =>
          totalAVencer > 0 ? `${Math.min(100, Math.max(valor > 0 ? 2 : 0, (valor / totalAVencer) * 100))}%` : "0%";

        return (
          <div key={indice} className="scr-container">
            {/* HEADER */}
            <div className="scr-header">
              <div className="scr-header-left">
                <div className="scr-icon-box">
                  <i className="pi pi-file"></i>
                </div>
                <div>
                  <h1 className="scr-header-title">Resumo Comportamental de Pagamento</h1>
                  <p className="scr-header-subtitle">Data-base: {formatarDataBase(dataBase?.dtb)}</p>
                </div>
              </div>
            </div>

            {/* ROW 1: CARDS */}
            <div className="scr-cards-grid">
              <div className="scr-card scr-card-green">
                <div className="scr-card-header">
                  <i className="pi pi-building scr-card-icon"></i> INSTITUIÇÕES
                </div>
                <div className="scr-card-value">{String(dataBase?.qtdIfs ?? "Não informado")}</div>
                <div className="scr-card-label">bancos/ financeiras</div>
              </div>

              <div className="scr-card scr-card-green">
                <div className="scr-card-header">
                  <i className="pi pi-file-edit scr-card-icon"></i> OPERAÇÕES
                </div>
                <div className="scr-card-value">{String(operacoes.length)}</div>
                <div className="scr-card-label">total de contratos</div>
              </div>

              <div className="scr-card scr-card-blue">
                <div className="scr-card-header">
                  <i className="pi pi-dollar scr-card-icon-lg"></i> EXPOSIÇÃO TOTAL DE CRÉDITO
                </div>
                <div className="scr-card-value-lg">{formatarParaMoedaBRL(riscoTotal)}</div>
                <div className="scr-card-label">crédito tomado + limites</div>
              </div>
            </div>

            {/* ROW 2: INFO E RESPONSABILIDADES */}
            <div className="scr-info-grid">
              <div className="scr-section">
                <div className="scr-section-title">
                  <div className="scr-section-title-line"></div>
                  Informações do Cliente
                </div>
                <div className="scr-info-list">
                  <div className="scr-info-item">
                    <span className="scr-info-label"><i className="pi pi-id-card scr-card-icon"></i> Documento</span>
                    <span className="scr-info-value">{documento ? maskCpfCnpj(documento) : "Não informado"}</span>
                  </div>
                  <div className="scr-info-item">
                    <span className="scr-info-label"><i className="pi pi-calendar scr-card-icon"></i> Data Base</span>
                    <span className="scr-info-value">{formatarDataBase(dataBase?.dtb)}</span>
                  </div>
                  <div className="scr-info-item">
                    <span className="scr-info-label"><i className="pi pi-calendar-plus scr-card-icon"></i> Início Relacionamento</span>
                    <span className="scr-info-value">{formatarDataISO(dataBase?.dtbIniRel)}</span>
                  </div>
                  <div className="scr-info-item">
                    <span className="scr-info-label"><i className="pi pi-file scr-card-icon"></i> Documentos Processados</span>
                    <span className="scr-info-value">{percentFormatter(String(dataBase?.docProc ?? 0))}</span>
                  </div>
                  <div className="scr-info-item">
                    <span className="scr-info-label"><i className="pi pi-clock scr-card-icon"></i> Volume Processado</span>
                    <span className="scr-info-value">{percentFormatter(String(dataBase?.volProc ?? 0))}</span>
                  </div>
                </div>
              </div>

              <div className="scr-section">
                <div className="scr-section-title">
                  <div className="scr-section-title-line"></div>
                  Responsabilidades
                </div>
                <div className="scr-info-list">
                  <div className="scr-info-item">
                    <span className="scr-info-label">Carteira de Crédito</span>
                    <span className="scr-info-value scr-value-green">{formatarParaMoedaBRL(carteiraCredito)}</span>
                  </div>
                  <div className="scr-info-item">
                    <span className="scr-info-label">Coobrigação Assumida</span>
                    <span className="scr-info-value scr-value-green">{formatarParaMoedaBRL(coobrigacaoAssumida)}</span>
                  </div>
                  <div className="scr-info-item">
                    <span className="scr-info-label">Coobrigação Recebida</span>
                    <span className="scr-info-value scr-value-blue">{formatarParaMoedaBRL(coobrigacaoRecebida)}</span>
                  </div>
                  <div className="scr-info-item">
                    <span className="scr-info-label">Responsabilidade Total</span>
                    <span className="scr-info-value scr-value-purple">{formatarParaMoedaBRL(responsabilidadeTotal)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* ROW 3: Situação das Operações + Limites de Crédito, em duas colunas */}
            <div className="scr-two-col-grid scr-section-mb">
              {/* COLUNA ESQUERDA */}
              <div className="scr-col">
                <div className="scr-section scr-section-mb">
                  <div className="scr-section-title scr-section-title-mb24">
                    <div className="scr-section-title-line"></div>
                    Situação das Operações
                  </div>
                  <div className="scr-situacao-grid">
                    <div>
                      <div className="scr-situacao-value scr-situacao-value-green">{formatarParaMoedaBRL(totalAVencer)}</div>
                      <div className="scr-situacao-label">
                        <i className="pi pi-check-circle scr-situacao-value-green"></i> A Vencer
                      </div>
                    </div>
                    <div>
                      <div className="scr-situacao-value scr-situacao-value-orange">{formatarParaMoedaBRL(totalVencido) || "R$ 0,00"}</div>
                      <div className="scr-situacao-label">
                        <i className="pi pi-exclamation-circle scr-situacao-value-orange"></i> Vencido
                      </div>
                    </div>
                    <div>
                      <div className="scr-situacao-value scr-situacao-value-red">{formatarParaMoedaBRL(totalPrejuizo) || "R$ 0,00"}</div>
                      <div className="scr-situacao-label">
                        <i className="pi pi-exclamation-triangle scr-situacao-value-red"></i> Prejuízo
                      </div>
                    </div>
                  </div>
                </div>

                {/* Carteira a Vencer / Distribuição por prazo */}
                <div className="scr-detalhamento-card scr-section-mb">
                  <div className="scr-detalhamento-header">
                    <div className="scr-header-left">
                      <div className="scr-icon-box">
                        <i className="pi pi-chart-line"></i>
                      </div>
                      <div>
                        <h2 className="scr-detalhamento-title">Detalhamento por Prazo</h2>
                        <p className="scr-detalhamento-subtitle">Análise temporal das operações</p>
                      </div>
                    </div>
                    <div className="scr-detalhamento-periodo">
                      <i className="pi pi-clock"></i>
                      <span>Data-base: {formatarDataBase(dataBase?.dtb)}</span>
                    </div>
                  </div>

                  <div className="scr-detalhamento-card">
                    <div className="scr-detalhamento-card-header">
                      <div className="scr-icon-box-sm">
                        <i className="pi pi-arrow-up-right"></i>
                      </div>
                      <div>
                        <h3 className="scr-detalhamento-card-title">Carteira a Vencer</h3>
                        <p className="scr-detalhamento-card-subtitle">Distribuição por prazo</p>
                      </div>
                    </div>

                    <div className="scr-info-list">
                      {FAIXAS_CARTEIRA_VENCER.map((faixa, faixaIdx) => {
                        const valorFaixa = faixa.codigos.reduce((soma, codigo) => soma + (porCodigo[codigo] || 0), 0);
                        const ultima = faixaIdx === FAIXAS_CARTEIRA_VENCER.length - 1;
                        return (
                          <div key={faixa.label} className={`scr-bar-container${ultima ? " scr-bar-container-last" : ""}`}>
                            <div className="scr-bar-header">
                              <span>{faixa.label}</span>
                              <span className="scr-bar-value" style={{ color: faixa.cor }}>{formatarParaMoedaBRL(valorFaixa)}</span>
                            </div>
                            <div className="scr-bar-track">
                              <div className="scr-bar-fill" style={{ width: larguraBarra(valorFaixa), backgroundColor: faixa.cor }}></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="scr-TotalVencer-container">
                      <span className="scr-TotalVencer-label">Total a Vencer</span>
                      <span className="scr-TotalVencer-value">{formatarParaMoedaBRL(totalAVencer)}</span>
                    </div>
                  </div>
                </div>

                {/* Detalhamento das Operações por Prazo - tudo que não for limite */}
                <div className="scr-modalidades-section scr-section-mb">
                  <div className="scr-detalhamento-header">
                    <div className="scr-header-left">
                      <div className="scr-icon-box">
                        <i className="pi pi-sitemap"></i>
                      </div>
                      <div>
                        <h2 className="scr-detalhamento-title">Detalhamento das Operações por Prazo</h2>
                        <p className="scr-detalhamento-subtitle">Operações de crédito por modalidade e código de vencimento</p>
                      </div>
                    </div>
                  </div>

                  <div className="scr-info-list pb-2">
                    {modalidadesOutras.map(([mod, vencimentos]) => (
                      <ModalidadeCard key={mod} mod={mod} vencimentos={vencimentos} />
                    ))}
                  </div>
                </div>
              </div>

              {/* COLUNA DIREITA */}
              <div className="scr-col">
                <div className="scr-section scr-section-mb">
                  <div className="scr-section-title scr-section-title-mb24">
                    <div className="scr-section-title-line"></div>
                    Limites de Crédito
                  </div>
                  <div className="scr-situacao-grid">
                    <div>
                      <div className="scr-situacao-value" style={{ color: "#10b981" }}>{formatarParaMoedaBRL(totalLimites)}</div>
                      <div className="scr-situacao-label">
                        <i className="pi pi-chart-line" style={{ color: "#10b981" }}></i> Total
                      </div>
                    </div>
                    <div>
                      <div className="scr-situacao-value" style={{ color: "#183462" }}>{formatarParaMoedaBRL(limitesAte360)}</div>
                      <div className="scr-situacao-label">
                        <i className="pi pi-clock" style={{ color: "#183462" }}></i> Até 360 dias
                      </div>
                    </div>
                    <div>
                      <div className="scr-situacao-value" style={{ color: "#183462" }}>{formatarParaMoedaBRL(limitesAcima360)}</div>
                      <div className="scr-situacao-label">
                        <i className="pi pi-clock" style={{ color: "#183462" }}></i> Acima 360 dias
                      </div>
                    </div>
                  </div>
                </div>

                {/* Detalhamento das Operações por Prazo - tudo que for limite */}
                <div className="scr-modalidades-section scr-section-mb">
                  <div className="scr-detalhamento-header">
                    <div className="scr-header-left">
                      <div className="scr-icon-box">
                        <i className="pi pi-sitemap"></i>
                      </div>
                      <div>
                        <h2 className="scr-detalhamento-title">Detalhamento das Operações por Prazo</h2>
                        <p className="scr-detalhamento-subtitle">Limites de crédito por modalidade e código de vencimento</p>
                      </div>
                    </div>
                  </div>

                  {modalidadesLimite.length > 0 && (
                    <div className="scr-info-list pb-2">
                      <div className="scr-modalidade-card">
                        <div className="scr-modalidade-header">
                          <div className="scr-modalidade-title">
                            <i className="pi pi-tag"></i> {DOMINIO_MODALIDADES["19"]}
                          </div>
                          <div className="scr-modalidade-total">
                            {formatarParaMoedaBRL(totalLimiteModalidades)}
                          </div>
                        </div>

                        <div className="scr-info-list">
                          {modalidadesLimite.map(([mod, vencimentos]) => (
                            <ModalidadeCard key={mod} mod={mod} vencimentos={vencimentos} />
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Garantias */}
                {Object.keys(garantias).length > 0 && (
                  <div className="scr-section scr-section-mb">
                    <div className="scr-section-title scr-section-title-mb24">
                      <div className="scr-section-title-line"></div>
                      Garantias Vinculadas
                    </div>
                    <div className="scr-info-list">
                      {Object.entries(garantias).map(([tipo, qtd]) => (
                        <div className="scr-info-item" key={tipo}>
                          <span className="scr-info-label">{TIPO_GARANTIA[tipo] || `Tipo ${tipo}`}</span>
                          <span className="scr-info-value">{qtd}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
};
